import {
  ButtonPrimary,
  DataTable,
  IconClose,
} from '@skf/bt-wind-rail-commonlibrary';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getTaxonomyTemplate, uploadTaxonomy } from '../../../api/taxonomy';
import { useLoadingScreen } from '../../../hooks/useLoadingScreen';
import { useToast } from '../../../hooks/useToast';
import { downloadFile } from '../../../services/fileService';
import { getUserEmail } from '../../../services/tokenDataService';
import { StoreState } from '../../../store/store';
import { fetchUploadTaxonomy } from '../../../store/taxonomy/actions';
import { ITaxonomyState } from '../../../store/taxonomy/store';
import { EXCEL_APPLICATION_TYPE } from '../../../store/taxonomy/types';

import style from './TaxonomyUpload.module.scss';

export const TaxonomyUpload = () => {
  const { setLoading } = useLoadingScreen();
  const { addToast } = useToast();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { taxonomyUpload } = useSelector<StoreState, ITaxonomyState>(
    (selector) => selector.taxonomy
  );

  const [taxonomyFile, setTaxonomyFile] = useState<File>();

  useEffect(() => {
    if (!taxonomyUpload) {
      dispatch(fetchUploadTaxonomy(getUserEmail()));
    } else {
      setLoading(false);
    }
  }, [taxonomyUpload]);

  const handleInputTaxonomyChange = (e) => {
    const files = Array.from<File>(e.target.files);
    if (files.length > 0) {
      setTaxonomyFile(files[0]);
    }
  };

  const uploadTaxonomyClick = (file: File) => {
    const progressCallback = () => {
      addToast({
        message: t('Upload_Taxonomy_Progress'),
        status: 'warning',
      });
    };
    const okCallback = () => {
      addToast({
        message: t('Upload_Taxonomy_Ok'),
        status: 'ok',
      });
      clearTaxonomySelected();
      reloadTaxonomyHistoric();
    };
    const failedCallback = () => {
      addToast({
        message: t('Upload_Taxonomy_Failed'),
        status: 'danger',
      });
    };

    const asyncUploadTaxonomy = async () => {
      const userEmail = getUserEmail();
      if (!userEmail) return;
      await uploadTaxonomy(
        file,
        userEmail!,
        progressCallback,
        okCallback,
        failedCallback
      );
    };

    asyncUploadTaxonomy();
  };

  const downloadTaxonomyTemplateClick = () => {
    const downloadTaxonomyAsync = async () => {
      const blobResult = await getTaxonomyTemplate();
      const blobExcel = new Blob([blobResult], {
        type: EXCEL_APPLICATION_TYPE,
      });
      const taxonomyTemplateUrl = URL.createObjectURL(blobExcel);
      downloadFile(taxonomyTemplateUrl, 'TaxonomyTemplate');
    };

    downloadTaxonomyAsync();
  };

  const clearTaxonomySelected = () => {
    setTaxonomyFile(undefined);
  };

  const reloadTaxonomyHistoric = () => {
    setLoading(true);
    dispatch(fetchUploadTaxonomy(getUserEmail()));
  };

  const fileNameCell = (name, url) => {
    return (
      <div className={style.fileNameCell} onClick={() => downloadFile(url, url)}>
        {name}
      </div>
    );
  };


  const commentsCell = (comments: string[], fileName: string) => {
    if (comments.length > 4) {
      const liComments: JSX.Element[] = [];
      for (let i = 0; i < 4; i++) {
        liComments.push(<li>{comments[i]}</li>);
      }
      const fileNameNoExtension =  fileName.substring(0, fileName.lastIndexOf('.'))
      const bloblURL = downloadCommentsFile(comments);
      liComments.push(<li className={style.downloadCommentsFileLi} ><a href={bloblURL} download={fileNameNoExtension}>{t("Upload_Taxonomy_Too_Many_Comments")}</a></li>);
      return (<>{liComments}</>)
    } else {
      return (comments.map(x => (<li>{x}</li>)))
    }

  };

  const downloadCommentsFile = (comments: string[]): string => {
    const formattedComments = comments.map(x => (x + '\n'))
    var blob = new Blob([...formattedComments],
      { type: "text/plain;charset=utf-8" });

    return URL.createObjectURL(blob);
  }

  const getColums = () => {
    const columnsHistoric: any[] = [
      {
        Header: t('HistoricFileName'),
        accessor: 'fileName',
        className: style.columnThree,
        Cell: (data) => {
          const { row: { original = {} } = {} } = data;
          const { fileName = '', fileURL = '' } = original;
          return fileNameCell(fileName, fileURL);
        },
      },
      {
        Header: t('HistoricLoadTime'),
        accessor: 'loadTime',
        className: style.columnThree,
      },
      {
        Header: t('HistoricStatus'),
        accessor: 'status',
        className: style.columnThree,
      },
      {
        Header: t('HistoricComments'),
        accessor: 'comments',
        className: style.columnThree,
        Cell: (data) => {
          const { row: { original = {} } = {} } = data;
          return <ul className={style.taxonomyUploadMessages}>{(commentsCell(original.comments, original.fileName))}</ul>
        },
      },
    ];

    return columnsHistoric;
  };

  const renderTableOrMessage = () => {
    if (taxonomyUpload && taxonomyUpload.length > 0) {
      return (
        <div className={style.uploadTaxonomyHistoric}>
          <DataTable
            columns={getColums()}
            data={taxonomyUpload}
            className={style.table}
            pagination={20}
          />
        </div>
      );
    } else {
      return (
        <div className={style.uploadTaxonomyNoHistoricMessage}>
          {t('NoHistoricMessage')}
        </div>
      );
    }
  };

  const renderUploadSelectButtons = () => {
    return taxonomyFile ? (
      <>
        <div className={style.taxonomyFileInfo}>
          <IconClose
            onClick={() => clearTaxonomySelected()}
            className={style.cursorPointer}
          />
          <div className={style.taxonomyFileName}>{taxonomyFile?.name}</div>
        </div>
        <div className={style.buttonUpload}>
          <ButtonPrimary onClick={() => uploadTaxonomyClick(taxonomyFile)}>
            <label className={style.uploadTaxonomyLabel}>
              {t('Upload_Taxonomy')}
            </label>
          </ButtonPrimary>
        </div>
      </>
    ) : (
      <>
        <div
          className={`${style.taxonomyFileInfo} ${style.taxonomyFileTemplate}`}
          onClick={() => downloadTaxonomyTemplateClick()}
        >
          <div className={style.taxonomyFileName}>
            {t('Download_TaxonomyTemplate')}
          </div>
        </div>
        <div className={style.buttonUpload}>
          <ButtonPrimary>
            <label
              htmlFor="uploadTaxonomy"
              className={style.uploadTaxonomyLabel}
            >
              {t('Select_Taxonomy')}
            </label>
            <input
              id="uploadTaxonomy"
              className={style.uploadTaxonomyInput}
              type="file"
              accept={EXCEL_APPLICATION_TYPE}
              onChange={(e) => handleInputTaxonomyChange(e)}
            />
          </ButtonPrimary>
        </div>
      </>
    );
  };

  return (
    <div className={style.uploadTaxonomyContainer}>
      <div className={style.uploadTaxonomyButtons}>
        {renderUploadSelectButtons()}
      </div>
      {renderTableOrMessage()}
    </div>
  );
};
